'use client';

import { ArrowLeft, Package, Github } from 'lucide-react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { GITHUB_URL, NPM_PACKAGE_URL } from '@/lib/site';

type Release = {
  version: string;
  date: string;
  title: string;
  changes: string[];
};

const releases: Release[] = [
  {
    version: '1.6.8',
    date: '2026-07-01',
    title: 'SQLi heuristic: community benchmark 68.1% → 97.7% TPR',
    changes: [
      'Quick-pattern regex fast-path now runs before the input.length<5 gate, which previously skipped short-but-structural payloads (\'--, \'#) before they ever reached the regexes.',
      'New signatures: double-quote " OR "/" AND " variants, quote+block-comment, bare boolean probes (OR 1=0, AND x=y), standalone HAVING 1=1, bare ORDER BY N enumeration, time-based blind functions (SLEEP(), BENCHMARK(), WAITFOR DELAY, RANDOMBLOB()), RLIKE(), @@version, information_schema, CAST/CONVERT(...SELECT...), CASE WHEN…THEN, CHAR()/CHR() concatenation, broadened DROP TABLE/FUNCTION/….',
      'Deliberately does not flag bare single-symbol fragments (\', =, ==) - indistinguishable from benign input, would reintroduce false positives.',
      'Measured v1.6.8: community suite SQLi 68.1% → 97.7% TPR (18 FN, down from 251), XSS unchanged at 96.1%, 0% FPR on all three suites. Core/extended unchanged at 100% TPR / 0% FPR.',
    ],
  },
  {
    version: '1.6.7',
    date: '2026-06-29',
    title: 'Community benchmark suite & SQLi blind-probe fix',
    changes: [
      'SQLi heuristic fix: restored detection of quote-break ORDER BY / GROUP BY column-enumeration probes (1\' ORDER BY 1--, GROUP BY … HAVING 1=1--) and nested-paren blind tautologies (1\')) OR ((\'1\'=\'1) without re-introducing benign false positives on free text with -- or #.',
      'New community benchmark suite: third-party payloads from PayloadsAllTheThings and HttpParamsDataset (~1,494 samples). Run with npm run benchmark:community; refresh with npm run benchmark:import-community. benchmark:all now runs core + extended + community.',
      'Measured v1.6.7: curated extended 100% TPR / 0% FPR on all four datasets; community (honest stress test) SQLi 68.1%, XSS 96.1%, 0% FPR. Community suite runs in CI on every test pass.',
      'Benchmarks page updated with three suites and honest coverage messaging - no completeness guarantees.',
    ],
  },
  {
    version: '1.6.6',
    date: '2026-06-28',
    title: 'Auth safe harbor & outbound egress (production false-positive fixes)',
    changes: [
      'Auth endpoint safe harbor: POST/PUT/PATCH on /login, /register, /auth, etc. no longer blocked for normal credential submission (wrong password → app\'s 401, not Silker 403).',
      'Data leakage password exclusion fix: placeholder regex now uses $ anchor for consistent password-field handling.',
      'Outbound egress guard rewrite: third-party detection runs only on outgoing fetch() traffic with URL-only decisions; incoming Express routes are out of scope. Threat type renamed to Untrusted Outbound.',
      'SilkerEvent.direction: incoming (Express middleware) vs outgoing (fetch hook) for direction-aware detectors.',
    ],
  },
  {
    version: '1.6.5',
    date: '2026-06-27',
    title: 'AI Detection Layer (v3): semantic detection, response inspection, streaming guardrails & AI Copilot',
    changes: [
      'New semantic prompt-injection layer (semanticThreatScore): a lightweight, fully local, edge-safe model that runs alongside the signature engine on LLM routes. It hashes words, word-bigrams and char 3-grams into a vector and compares it to attack/benign centroids, catching paraphrased and novel injection variants that no regex matches - no external model, no API call, ~0ms. A conservative threshold plus a benign margin keep false positives at zero. Toggle: semanticDetection (default on).',
      'New outbound response inspection: outgoing responses are scanned for leaked secrets (provider API keys, JWT, private keys, DB connection strings) and PII (email/phone) before they reach the client, generating Data Leakage events. Wired into the SDK fetch hook, the Cloudflare Worker and the self-host container; binary content types are skipped. Toggle: responseInspection (default on).',
      'New streaming LLM guardrails: a TransformStream inspects streamed model output (SSE / ndjson) token-by-token and cuts off the stream the moment a secret/PII leak or a successful jailbreak surfaces - before the payload completes. A boundary-safe holdback prevents a secret split across chunks from leaking early. Edge-safe (Worker + Node 18+). Toggle: streamingGuardrails (default on).',
      'New AI Copilot - explain & fix (dashboard): every blocked threat can be enriched on demand with a plain-English explanation of the attack plus a concrete, copy-pasteable code fix, generated by AI reasoning over the captured request context. Shown in the AI Security threat drawer (Pro+).',
      '+16 new SDK unit tests; full SDK suite at 572 green.',
    ],
  },
  {
    version: '1.6.1',
    date: '2026-06-26',
    title: 'Security & reliability hardening (fewer false positives, safer defaults)',
    changes: [
      'SQLi heuristic - eliminated false positives that could block legitimate traffic: line/hash comment markers (--, #) are matched only in SQL context, and OR/AND tautologies now require identical operands around the equality. Benign query strings like "?q=red or blue&sort=name" pass cleanly while real injections (UNION SELECT, \' OR \'1\'=\'1, ; DROP TABLE) stay blocked; all benchmark suites remain at 100% TPR / 0% FPR.',
      'Bounded ban-map memory: expired bans are swept periodically and a soft cap (MAX_BAN_ENTRIES = 50000) evicts the oldest entries, preventing unbounded growth under distributed scanning.',
      'Fetch hook no longer monitors its own traffic: internal SDK requests (telemetry/sync, identified by the x-silker-client-version header) bypass interception, avoiding a self-monitoring feedback loop and telemetry noise.',
      'Hardened remote config: only known feature keys from the dashboard response are applied (unknown keys ignored), and a new optional remoteConfigFloor lists critical detectors that remote config can enable but never silently disable.',
      'trustProxy startup warning: when left at its default, the Express middleware logs a one-time warning that client IP is derived from spoofable proxy headers when not actually behind a trusted proxy.',
    ],
  },
  {
    version: '1.6.0',
    date: '2026-06-25',
    title: '100% detection across all extended benchmarks (0 false negatives, 0 false positives)',
    changes: [
      'Prompt injection non-LLM-route: 97.6% → 100.0% TPR at 0% FPR - fixed 7 false negatives across system-prompt extraction, leetspeak/letter-spacing ("print your hidden instructions"), encoding obfuscation (hex-decoded "ignore all prev") and chain manipulation ("| ignore previous |", "; ignore safety;").',
      'SQL injection: 96.9% → 100.0% TPR - MySQL # hash comment now detected, AND-tautology detection added mirroring the OR lookahead, and nested-parenthesis blind injection (1\')) OR ((\'1\'=\'1) caught by extending the OR lookahead window.',
      'All four detection suites now at 100% TPR / 0% FPR on both the core (210 samples, CI gate) and extended (1012 samples) benchmarks.',
    ],
  },
  {
    version: '1.5.2',
    date: '2026-06-22',
    title: 'Extended-suite prompt-injection detection',
    changes: [
      'Multi-view analysis haystack: leetspeak normalization, spaced-letter collapse, mixed-script Cyrillic homoglyph folding, plus ROT13/hex/base64/escape decode-and-rescan before pattern matching.',
      'Expanded extraction, jailbreak, malicious roleplay, data exfiltration, multilingual, delimiter and chain-manipulation patterns. Chain "first, ignore" now requires injection context so benign data-cleaning phrases pass.',
      'Extended benchmark (1012 samples): prompt-injection LLM-route TPR 71.4% → 100% at 0% FPR; core suite 100% TPR / 0% FPR on all datasets.',
    ],
  },
  {
    version: '1.5.1',
    date: '2026-06-22',
    title: 'Apache-2.0 license',
    changes: [
      'Relicensed from proprietary (UNLICENSED) to Apache-2.0. Full license text in LICENSE; package.json and docs updated. No runtime/code changes.',
    ],
  },
  {
    version: '1.5.0',
    date: '2026-06-22',
    title: 'Obfuscation-resistant prompt-injection detection',
    changes: [
      'Input normalization before detection: detectPromptInjection now strips zero-width / invisible / bidi-control characters and applies Unicode NFKC folding, so attacks hidden behind zero-width separators ("i\u200Bg\u200Bn\u200Bo\u200Br\u200Be all previous instructions") or fullwidth homoglyphs are surfaced as plain text and blocked. Exposed as normalizeForDetection().',
      'Base64 and escape decode-and-rescan: base64 blobs and \\uXXXX / \\xXX escape sequences are decoded and re-scanned by the same heuristics, so a payload like "base64: aWdub3Jl..." (decodes to "ignore all previous instructions and comply") is caught instead of scoring as a harmless blob. Decoding is capped and runtime-agnostic (Edge atob / Node Buffer).',
      'Stronger token-smuggling signal: invisible characters wedged between word characters are now treated as an unambiguous high-severity obfuscation signature.',
      'Broader multilingual coverage: instruction-override patterns added for German, Portuguese, Italian, Korean and Arabic (joining Russian, Chinese, Japanese, Spanish, French); multilingual and delimiter-injection matches re-weighted to high so they are blocked on non-LLM routes too.',
      'Measured detection jump: on the expanded adversarial benchmark both prompt-injection policies now reach ~96% detection rate (TPR) at 0% false positives - the non-LLM-route TPR was ~76% before. Remaining misses (leetspeak, Cyrillic homoglyphs, Hindi) are documented known limitations.',
      'Hardened benchmark: prompt-injection dataset expanded with 16 new adversarial attacks and 10 benign false-positive traps; CI quality gate raised (non-LLM-route TPR >= 0.90).',
    ],
  },
  {
    version: '1.4.0',
    date: '2026-06-16',
    title: 'Silker Shield - client-side website protection',
    changes: [
      'New Silker Shield product: a lightweight JS snippet you embed on any website (no server required) that adds a live security layer in the browser.',
      'Protection modules (all toggle-able per site from the dashboard): Link Guard (real-time phishing and malicious URL scanning via blocklist + heuristics), DOM Watch (detects page tampering and injected scripts), Clickjack Protection (blocks iframe-based UI redressing), Form Guard (intercepts credential harvesting on suspicious forms), Bot Detection (canvas/timing fingerprint to surface headless browsers), Tamper Guard, and Console Warn.',
      'Trusted-site badge: configurable theme (dark/light), accent color, corner position, and pulse animation - gives visitors a visible proof-of-protection signal.',
      'Event pipeline: pageview, alert, link_blocked and bot events are buffered in-memory and batch-flushed to Supabase every 15 s via a SECURITY DEFINER RPC; counters survive brief DB outages and retry automatically.',
      'Config delivery via /shield/config/:siteId: dashboard changes propagate to the running embed within ~60 s with no redeploy.',
      'Fail-open design: if the Silker backend is unreachable the embed degrades silently - the protected page always loads.',
    ],
  },
  {
    version: '1.3.4',
    date: '2026-06-11',
    title: 'Scanner Trap - honeypot paths with instant bot ban',
    changes: [
      'New scannerTrapDetection feature (default on): requests to well-known exploit/scanner paths (/.env, /.git/, /.aws/, /.ssh/, /wp-login.php, /wp-admin, /xmlrpc.php, /phpmyadmin, /cgi-bin/, /actuator/, /backup.sql, */shell.php and more) are flagged as a new "Scanner Probe" threat (high severity).',
      'Instant ban on trap hit: with IP banning enabled (default), the scanner\u2019s IP is banned the moment it touches a trap path - before it can reach a real attack surface. Pure pathname matching, ~0ms, near-zero false positives on Node/Next stacks.',
      'Dashboard-manageable: toggle "Scanner Trap" in the Configuration panel; probe events appear in Activity/Threats with their category (env-probe, cms-probe, vcs-probe, admin-probe, backup-probe).',
      'Deliberately excluded from traps: /.well-known/ (ACME, security.txt) and /admin (common legitimate panels).',
    ],
  },
  {
    version: '1.3.3',
    date: '2026-06-10',
    title: 'Fewer prompt-injection false positives on LLM routes',
    changes: [
      'New LLM-route blocking policy: LLM routes now block on medium+ severity, or a low-severity match carrying a high-confidence override signal (shouldBlockPromptInjectionOnLlmRoute). Benign UX roleplay ("act as a translator", "pretend you are a pirate") passes; roleplay combined with an override/jailbreak signal still escalates and is blocked.',
      'Detection benchmark suite: LLM-route false-positive rate dropped from 24.4% to 0.0%, with TPR staying high (98.3% → 94.9%); SQLi/XSS unchanged (100% / 0%).',
      'Compound scoring via overrideSignal: detectPromptInjection now flags whether a high-confidence category (override, jailbreak, extraction, exfiltration, delimiter/system manipulation, encoding, multilingual, obfuscation) matched, vs. standalone persona-roleplay.',
      'Fixed: "new (instructions|role|mode|system)" over-match no longer fires on benign nouns like "the new instructions for assembling the furniture"; it now requires an injection-context anchor.',
    ],
  },
  {
    version: '1.3.2',
    date: '2026-06-10',
    title: 'Detection correctness, proxy safety & distributed state',
    changes: [
      'Fixed multipart upload false positives: multipart requests without parsed file metadata are no longer flagged; only real indicators (dangerous extensions/filenames, malicious magic bytes, path traversal) trigger the file-upload detector.',
      'Unified feature defaults via a single shared isFeatureEnabled helper used by both isAnomaly and detectThreatType.',
      'Unified payload scan limit: one shared DEFAULT_SCAN_LIMIT_BYTES (100KB) across the Express hook, the Edge core, and isAnomaly; maxPayloadSize is honored everywhere.',
      'New outboundSsrfProtection feature flag (default true) - explicit, documented SSRF checking of outgoing fetch() calls, separate from incoming ssrfDetection.',
      'New trustProxy option (default true) - set trustProxy: false when not behind a proxy so the spoofable x-forwarded-for header is ignored.',
      'Fixed stateful /g regexes: detectJailbreak no longer returns intermittent false negatives from lastIndex leakage.',
      'Secrets in request bodies now block regardless of HTTP method (was GET-only); password fields on auth endpoints remain allowed.',
      'New pluggable state store: SilkerStateStore interface + InMemoryStateStore to share rate-limit counters and IP bans across instances.',
      'Fixed stale client version header: x-silker-client-version now reports the real package version.',
      'Edge adapter applies remote config: nextMiddleware now applies dashboard-managed features and banned IPs from the ingest response.',
      'Extensible threat intel lists via threatIntel: { ips, domains }; added GitHub Actions CI (tests + build on PRs and pushes).',
    ],
  },
  {
    version: '1.3.0',
    date: '2026-06-09',
    title: 'Next.js Edge adapter & deeper LLM/AI threat detection',
    changes: [
      'New @silker-ai/agent/next subpath export: nextMiddleware(options?) returns an App Router / Edge-runtime compatible handler built on the edge-safe core (parity with the Cloudflare Worker, fail-open, fire-and-forget telemetry).',
      'Expanded LLM/AI prompt-injection detection with classified subtypes via classifyPromptInjection: jailbreak (high), system_prompt_extraction (high), instruction_override (high), data_exfiltration_via_llm (critical).',
      'Broader LLM route coverage: /v1/responses, /api/agent, /api/copilot, /api/assistant, /messages.',
    ],
  },
  {
    version: '1.2.0',
    date: '2026-06-09',
    title: 'Dashboard-managed detection config',
    changes: [
      'The dashboard is now the source of truth for detection features: toggling protection in the Silker panel updates the running SDK on the next telemetry sync (~5s), no redeploy needed.',
      'Config is delivered over the existing ingest response (no extra requests); opt out with remoteConfig: false to keep config in code/env only.',
    ],
  },
  {
    version: '1.1.0',
    date: '2026-06-09',
    title: 'Zero-config init & safer defaults',
    changes: [
      'middleware() works with no arguments: SILKER_API_KEY, SILKER_APP_ID and SILKER_ENDPOINT are resolved from env.',
      'Telemetry no longer requires appId (platform resolves the app from the API key).',
      'Safer defaults: high-false-positive detectors (CSRF, zero trust, access control, SSRF-incoming, IDOR, compliance, threat intel) are now opt-in; core protections (SQLi, XSS, path traversal, prompt injection, rate limit, data leakage, file upload) stay on.',
      'Outgoing fetch hook is monitor-only by default; blocking requires blockOutgoing: true.',
      'Fixed: dataLeakageDetection: false now actually disables blocking; per-request context moved to AsyncLocalStorage (no more cross-request IP mix-ups under load).',
      'Removed legacy /api/dashboard/sync call and the unused http-proxy dependency.',
    ],
  },
  {
    version: '1.0.0',
    date: '2026-06-09',
    title: 'Initial public release',
    changes: [
      'Runtime middleware for Next.js, Express, and Node.js.',
      'OWASP Top 10 detection (SQLi, XSS, SSRF, prompt injection, and more).',
      'Real-time telemetry to the Silker AI platform.',
      'Cloudflare Worker deployment option and Docker proxy container option.',
      '26 test suites.',
    ],
  },
];

export default function ChangelogPage() {
  return (
    <div className="min-h-screen bg-black">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-rose-500/5 rounded-full blur-3xl"></div>
      </div>

      <Header />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors group mb-8 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back</span>
        </Link>

        <div className="mb-12">
          <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-6">
            <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700"></div>
            Changelog
            <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700"></div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            <span className="bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent">
              Changelog
            </span>
          </h1>
          <p className="text-base text-neutral-400 max-w-2xl leading-relaxed mb-6">
            Changelog for{' '}
            <code className="text-rose-400">@silker-ai/agent</code> - the runtime detection SDK that powers
            Silker AI. Newest releases first.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={NPM_PACKAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-none border border-neutral-700 text-sm text-neutral-300 hover:border-rose-500/40 hover:text-white transition-colors"
            >
              <Package className="w-4 h-4 text-rose-400" />
              npm package
            </a>
            <a
              href={GITHUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-none border border-neutral-700 text-sm text-neutral-300 hover:border-rose-500/40 hover:text-white transition-colors"
            >
              <Github className="w-4 h-4 text-rose-400" />
              GitHub
            </a>
          </div>
        </div>

        <div className="relative">
          <div className="absolute left-[7px] top-2 bottom-2 w-px bg-neutral-800/70 md:left-[calc(8rem+7px)]"></div>

          <div className="space-y-10">
            {releases.map((release) => (
              <div key={release.version} className="relative pl-8 md:pl-0 md:grid md:grid-cols-[8rem_1fr] md:gap-8">
                <div className="md:text-right md:pr-8 md:relative">
                  <div className="absolute -left-[29px] top-1.5 w-3.5 h-3.5 rounded-full bg-rose-500/20 border border-rose-500/50 md:left-auto md:-right-[7px] md:translate-x-1/2"></div>
                  <div className="inline-flex items-center md:inline-block">
                    <span className="text-lg font-semibold text-white">v{release.version}</span>
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">{release.date}</div>
                </div>

                <section className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-6 mt-3 md:mt-0">
                  <h2 className="text-base font-semibold text-white mb-4">{release.title}</h2>
                  <ul className="space-y-3">
                    {release.changes.map((change, i) => (
                      <li key={i} className="flex gap-3 text-sm text-neutral-400 leading-relaxed">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-rose-500/60 flex-shrink-0"></span>
                        <span>{change}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
